import { readBoundedResponseBytes } from "@/lib/http/response-body";

import { normalizeBlogSearchText } from "@/lib/blog/search-lexical";

const DEFAULT_SUGGESTION_LIMIT = 8;
const MAX_SUGGESTION_LIMIT = 12;
const SEARCH_CLIENT_ID_STORAGE_KEY = "eclat_search_client_id";
const SEARCH_CLIENT_ID_PATTERN = /^[a-z0-9_-]{8,64}$/i;
const MAX_SUGGESTION_LENGTH = 200;

type FetchLiveBlogSuggestionsArgs = {
  query: string;
  category?: string | null;
  limit?: number;
  signal?: AbortSignal;
};

export type LiveSuggestionRequestError = Error & {
  status?: number;
  retryAfterSec?: number;
};

function parseRetryAfterSeconds(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
}

function normalizeSuggestions({
  rawSuggestions,
  query,
  limit,
}: {
  rawSuggestions: unknown;
  query: string;
  limit: number;
}): string[] {
  if (!Array.isArray(rawSuggestions)) return [];

  const normalizedQuery = normalizeBlogSearchText(query);
  const deduped: string[] = [];
  const seen = new Set<string>();

  for (const entry of rawSuggestions) {
    if (typeof entry !== "string") continue;
    const trimmed = entry.trim();
    if (!trimmed || trimmed.length > MAX_SUGGESTION_LENGTH) continue;
    const normalized = normalizeBlogSearchText(trimmed);
    if (!normalized) continue;
    if (normalized === normalizedQuery) continue;
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    deduped.push(trimmed);
    if (deduped.length >= limit) break;
  }

  return deduped;
}

function createSearchClientId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return `${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2)}`.slice(0, 32);
}

function getSearchClientId(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const existing = window.localStorage
      .getItem(SEARCH_CLIENT_ID_STORAGE_KEY)
      ?.trim();
    if (existing && SEARCH_CLIENT_ID_PATTERN.test(existing)) {
      return existing;
    }

    const nextId = createSearchClientId();
    if (!SEARCH_CLIENT_ID_PATTERN.test(nextId)) {
      return null;
    }
    window.localStorage.setItem(SEARCH_CLIENT_ID_STORAGE_KEY, nextId);
    return nextId;
  } catch {
    return null;
  }
}

export async function fetchLiveBlogSuggestions({
  query,
  category,
  limit = DEFAULT_SUGGESTION_LIMIT,
  signal,
}: FetchLiveBlogSuggestionsArgs): Promise<string[]> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [];
  const boundedLimit =
    Number.isSafeInteger(limit) && limit > 0
      ? Math.min(limit, MAX_SUGGESTION_LIMIT)
      : DEFAULT_SUGGESTION_LIMIT;

  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  const searchClientId = getSearchClientId();
  if (searchClientId) {
    headers["x-search-client-id"] = searchClientId;
  }

  const response = await fetch("/api/search/suggest", {
    method: "POST",
    body: JSON.stringify({
      category: category?.trim() || undefined,
      limit: boundedLimit,
      q: trimmedQuery,
    }),
    cache: "no-store",
    signal,
    headers,
  });

  if (!response.ok) {
    const error = new Error(
      `Live suggestion request failed (${response.status})`
    ) as LiveSuggestionRequestError;
    error.status = response.status;
    error.retryAfterSec = parseRetryAfterSeconds(
      response.headers.get("Retry-After")
    );
    throw error;
  }

  const body = await readBoundedResponseBytes(response, 64 * 1024, signal);
  if (!body.ok) throw new Error("Live suggestion response could not be read");
  const payload: unknown = JSON.parse(new TextDecoder().decode(body.bytes));
  if (
    typeof payload !== "object" ||
    payload === null ||
    Array.isArray(payload) ||
    !Array.isArray((payload as Record<string, unknown>).suggestions)
  ) {
    throw new Error("Live suggestion response was malformed");
  }

  return normalizeSuggestions({
    rawSuggestions: (payload as Record<string, unknown>).suggestions,
    query: trimmedQuery,
    limit: boundedLimit,
  });
}
